/**
 * Billing DTOs (REST /v1, docs/features/billing.md). A workspace is on one plan from the catalog (core `logic/billing`):
 * `founding` for every workspace that existed before billing (no limits, never billed), `starter` for new ones (a
 * 14-day trial without a card, then a flat Stripe subscription, monthly or yearly). Pro and Business slot in later.
 *
 * - `GET /v1/w/:ws/billing` (any member): the plan, its status, limits and current usage. `canManage` tells clients
 *   whether to offer checkout and the portal (owners and admins only).
 * - `POST …/billing/checkout` and `POST …/billing/portal` (owners and admins): a Stripe-hosted page to open. 503
 *   `billing_unavailable` while Stripe isn't configured.
 * Limits absent ⇒ unlimited. Sizes are bytes (decimal GB, like docs/architecture/storage-limits.md).
 */
import { z } from "zod";
import { Timestamp } from "./entities.ts";

/** Plans in the catalog. `founding`: workspaces from before billing (no limits, no billing). */
export const Plan = z.enum(["founding", "starter"]);
export type Plan = z.infer<typeof Plan>;

/**
 * - `trialing`: the free trial (no card yet, or a Stripe subscription still in its trial);
 * - `active`: paid up; `past_due`: a payment failed and Stripe is retrying (access stays 14 days from the first failure);
 * - `canceled`: the subscription ended or ends at `currentPeriodEnd`; `expired`: it ran out unpaid.
 */
export const BillingStatus = z.enum(["trialing", "active", "past_due", "canceled", "expired"]);
export type BillingStatus = z.infer<typeof BillingStatus>;

/** `locked`: read-only (reads, search, export, billing and sign-out work; sending, uploads, edits and huddles don't). */
export const BillingAccess = z.enum(["full", "locked"]);
export type BillingAccess = z.infer<typeof BillingAccess>;

export const BillingInterval = z.enum(["month", "year"]);
export type BillingInterval = z.infer<typeof BillingInterval>;

/** Who bills the subscription: Stripe (web and desktop), the App Store (iOS) or Google Play (Android). */
export const BillingSource = z.enum(["stripe", "app_store", "play"]);
export type BillingSource = z.infer<typeof BillingSource>;

/** What Stripe charges in: US dollars, or reais in Brazil (the BRL option on the same Stripe prices). */
export const BillingCurrency = z.enum(["usd", "brl"]);
export type BillingCurrency = z.infer<typeof BillingCurrency>;

/** What the plan allows. A missing field means unlimited. */
export const BillingLimits = z.object({
  /** Active people who aren't guests (agents never count). */
  members: z.number().int().nonnegative().optional(),
  /** Files storage pooled across the workspace, add-ons included. */
  storageBytes: z.number().int().nonnegative().optional(),
});
export type BillingLimits = z.infer<typeof BillingLimits>;

export const BillingUsage = z.object({
  /** What `limits.members` counts: active people who aren't guests, plus `pendingInvites`. */
  members: z.number().int().nonnegative(),
  /** Email invitations not accepted yet (each holds a seat; included in `members`). */
  pendingInvites: z.number().int().nonnegative(),
  /** Absent while the workspace has no cheap byte figure (docs/features/billing.md). */
  storageBytes: z.number().int().nonnegative().optional(),
});
export type BillingUsage = z.infer<typeof BillingUsage>;

export const WorkspaceBilling = z.object({
  plan: Plan,
  status: BillingStatus,
  /** The subscription's billing interval, once there is one. */
  interval: BillingInterval.optional(),
  /** The subscription's currency or, with none yet, what Checkout will charge this caller. Absent ⇒ usd. */
  currency: BillingCurrency.optional(),
  /** When the trial ends (trialing). */
  trialEndsAt: Timestamp.optional(),
  /** The end of the period paid for (renewal, or the last day when `cancelAtPeriodEnd`). */
  currentPeriodEnd: Timestamp.optional(),
  cancelAtPeriodEnd: z.boolean(),
  /** The first failed payment of the current `past_due` stretch (full access lasts 14 days from it). */
  pastDueSince: Timestamp.optional(),
  /** Since when the workspace is locked (data is kept 90 days from it). */
  lockedAt: Timestamp.optional(),
  access: BillingAccess,
  /** Storage add-ons on the subscription (+50 GB each on Starter). */
  storageAddons: z.number().int().nonnegative(),
  limits: BillingLimits,
  usage: BillingUsage,
  /** The caller may start checkout and open the billing portal (owners and admins). */
  canManage: z.boolean(),
  /** Who bills the subscription, once there is one. A store subscription is managed only in that store. */
  source: BillingSource.optional(),
  /** A store subscription: the name of the person whose App Store / Google Play account pays for it. */
  storeManagedBy: z.string().optional(),
  /** A store subscription: whether it's the caller's own store purchase (they can manage it in the store). */
  storeManagedByMe: z.boolean().optional(),
});
export type WorkspaceBilling = z.infer<typeof WorkspaceBilling>;

/** GET /v1/w/:ws/billing */
export const BillingResponse = z.object({ billing: WorkspaceBilling });
export type BillingResponse = z.infer<typeof BillingResponse>;

/** POST /v1/w/:ws/billing/checkout: Starter on this interval, with storage add-ons. */
export const CreateBillingCheckoutRequest = z.object({
  interval: BillingInterval,
  storageAddons: z.number().int().min(0).max(100).optional(),
});
export type CreateBillingCheckoutRequest = z.infer<typeof CreateBillingCheckoutRequest>;

/** A Stripe-hosted page to open (Checkout or the billing portal). */
export const BillingRedirectResponse = z.object({ url: z.string() });
export type BillingRedirectResponse = z.infer<typeof BillingRedirectResponse>;

/**
 * POST /v1/w/:ws/billing/store/token (owners and admins, iOS and Android): the token to tag a store purchase with
 * (StoreKit `appAccountToken`, Play `obfuscatedAccountId`), tying it to this workspace. 409 `billing_source_conflict`
 * while another source (or another store) bills the workspace.
 */
export const StorePurchaseTokenResponse = z.object({ token: z.string().uuid() });
export type StorePurchaseTokenResponse = z.infer<typeof StorePurchaseTokenResponse>;

/**
 * POST /v1/w/:ws/billing/store/verify (owners and admins): hand a finished store purchase to the server, which
 * verifies it with Apple or Google and updates the workspace's plan. App Store: the StoreKit 2 transaction's signed JWS
 * (`jwsRepresentation`); Google Play: the subscription id and purchase token. Also used by Restore purchases.
 * → BillingResponse. 409 `billing_source_conflict` when the workspace is billed elsewhere, or the purchase is tied to
 * another workspace.
 */
export const VerifyStorePurchaseRequest = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("app_store"),
    signedTransaction: z.string().min(1).max(20_000),
    /** The subscription's signed renewal info (StoreKit `Product.SubscriptionInfo.Status.renewalInfo`), when known. */
    signedRenewalInfo: z.string().min(1).max(20_000).optional(),
  }),
  z.object({ source: z.literal("play"), productId: z.string().min(1).max(200), purchaseToken: z.string().min(1).max(2_000) }),
]);
export type VerifyStorePurchaseRequest = z.infer<typeof VerifyStorePurchaseRequest>;
